import React, { useState, useContext, useEffect } from 'react';
import { WelletContext } from '../../../context/WelletContext';
import { getQueryString } from '../../../helpers/url';
import { getStripeOnboardingStatus } from '../../../services/withdrawal/withdrawalService';
import ContentApp from '../../../components/ContentApp';
import { CircleSpinner } from 'react-spinners-kit';
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../../../actions/appActions';
import { useDispatch, useSelector } from 'react-redux';
import styles from './StripeOnboarding.module.css';

const checkmarkSvg = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#12B76A"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    class="lucide lucide-circle-check-big w-8 h-8 text-green-600"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <path d="m9 11 3 3L22 4"></path>
  </svg>
);

const warningSvg = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#F79009"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    class="lucide lucide-triangle-alert w-8 h-8 text-orange-600"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
    <path d="M12 9v4"></path>
    <path d="M12 17h.01"></path>
  </svg>
);

const StripeOnboardingReturn = () => {
  const welletContext = useContext(WelletContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lang = getQueryString('lang');
  const token = getQueryString('t');
  const app = useSelector(state => state.app);

  const [isLoading, setIsLoading] = useState(true);
  const [withdrawalStatus, setWithdrawalStatus] = useState(null);

  useEffect(() => {
    if (lang) {
      dispatch(setLanguage(lang));
    }
  }, [lang]);

  const fetchOnboardingStatus = async () => {
    setIsLoading(true);
    const status = await getStripeOnboardingStatus(welletContext, token);
    setWithdrawalStatus(status);
    setIsLoading(false);
  };

  useEffect(() => {
    if (!token) {
      return;
    }
    fetchOnboardingStatus();
  }, [token]);

  if (isLoading) {
    return (
      <div className="loaderDiv">
        <CircleSpinner
          className="spinner-app mx-auto"
          style={{ margin: 'auto' }}
          size={40}
          color="var(--color-font-primary)"
        />
      </div>
    );
  }

  const isSuccess = withdrawalStatus?.isActive;
  const appName = withdrawalStatus?.appName;
  const onboardingLink = withdrawalStatus?.onboardingLink;

  if (isSuccess) {
    return <StripeOnboardingReturnSuccess t={t} appName={appName} app={app} />;
  } else {
    return (
      <StripeOnboardingReturnIncomplete
        t={t}
        appName={appName}
        onboardingLink={onboardingLink}
        app={app}
      />
    );
  }
};

const headerApp = ({ appName, app }) => {
  return (
    <div>
      <div className="d-flex justify-content-center align-items-center">
        <div className={styles.logoContainer}>
          <img src={app?.isoLogo?.value} alt={appName} />
        </div>
        <div className="font-125 fw-600 ml-3">{appName}</div>
      </div>
    </div>
  );
};

const StripeOnboardingReturnIncomplete = ({ appName, app, onboardingLink, t }) => {
  const handleClick = () => {
    window.location.href = onboardingLink;
  };
  return (
    <ContentApp>
      {headerApp({ appName, app })}
      <div className={`${styles.card} text-center mt-4`}>
        <div className={styles.checkmarkStyle}>
          <div className={styles.warningCircleStyle}>{warningSvg}</div>
        </div>
        <div className="fw-700 font-large">{t('onboardingReturn.incomplete.title')}</div>
        <div className="mt-4">{t('onboardingReturn.incomplete.subtitle')}</div>
        <div className="mt-4">{t('onboardingReturn.incomplete.subtitle2')}</div>
        <button className={styles.button} onClick={handleClick}>
          {t('onboardingReturn.incomplete.button')}
        </button>
        <div className={`${styles.warningBox} font-tiny mt-4`}>
          <div>
            <span className="fw-700">{t('onboardingReturn.incomplete.nextSteps')}</span>{' '}
            {t('onboardingReturn.incomplete.nextStepDescription')}
          </div>
        </div>
      </div>
    </ContentApp>
  );
};

const StripeOnboardingReturnSuccess = ({ appName, app, t }) => {
  return (
    <ContentApp>
      {headerApp({ appName, app })}
      <div className={`${styles.card} text-center mt-4`}>
        <div className={styles.checkmarkStyle}>
          <div className={styles.iconCircleStyle}>{checkmarkSvg}</div>
        </div>
        <div className="fw-700 font-large">{t('onboardingReturn.success.title')}</div>
        <div className="mt-4">{t('onboardingReturn.success.subtitle')}</div>
        <div className="mt-4">
          {t('onboardingReturn.success.subtitle2')}
          <span className="fw-700">{appName}</span>
          {t('onboardingReturn.success.subtitle3')}
        </div>
        <div className={`${styles.nextSteps} font-tiny mt-4`}>
          <div>
            <span className="fw-700">{t('onboardingReturn.success.nextSteps')}</span>{' '}
            {t('onboardingReturn.success.nextStepDescription', { appName })}
          </div>
        </div>
      </div>
    </ContentApp>
  );
};

export default StripeOnboardingReturn;
